/**
 * Custom: Upsell description
 * -----------------------------------------------------------------------------
 * Renders the upsell popup description as HTML when it arrives as rich text
 * JSON.
 *
 * The popup prints `upsell.description` with `v-html`. On the product page the
 * value comes from `snippets/format-product.liquid` (`metafield_tag`, so HTML),
 * but product cards loaded through the Storefront API pass the raw value of the
 * `product.upsell_description` rich text metafield, which is a JSON document
 * (`{"type":"root","children":[...]}`), so the popup showed the JSON itself.
 *
 * Subscribers run right after the compiled `upsell/setDescription` commit and
 * before Vue re-renders, so the JSON is never painted.
 */
(function() {
  'use strict'

  var MUTATION = 'upsell/setDescription'
  var BOOT_INTERVAL = 100
  var BOOT_ATTEMPTS = 100

  /**
   * Find the Vuex store on the mounted Vue app.
   * @returns {Object|null}
   */
  function findStore() {
    var element = document.getElementById('vue-element')

    if (!element || !element.__vue_app__) {
      return null
    }

    var app = element.__vue_app__

    if (!app.config || !app.config.globalProperties) {
      return null
    }

    return app.config.globalProperties.$store || null
  }

  /**
   * Escape text for use in HTML.
   * @param {String} value - Raw text.
   * @returns {String}
   */
  function escape(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  /**
   * Render rich text nodes, following Shopify's rich text schema.
   * @param {Array} nodes - Child nodes.
   * @returns {String}
   */
  function renderNodes(nodes) {
    return (nodes || []).map(renderNode).join('')
  }

  /**
   * Render one rich text node.
   * @param {Object} node - Rich text node.
   * @returns {String}
   */
  function renderNode(node) {
    if (!node || typeof node !== 'object') {
      return ''
    }

    var children = renderNodes(node.children)

    switch (node.type) {
      case 'root':
        return children

      case 'paragraph':
        return '<p>' + children + '</p>'

      case 'heading': {
        var level = Math.min(Math.max(parseInt(node.level, 10) || 2, 1), 6)

        return '<h' + level + '>' + children + '</h' + level + '>'
      }

      case 'list':
        return node.listType === 'ordered'
          ? '<ol>' + children + '</ol>'
          : '<ul>' + children + '</ul>'

      case 'list-item':
        return '<li>' + children + '</li>'

      case 'link': {
        var url = String(node.url || '')

        if (!/^(https?:|mailto:|tel:|\/|#)/i.test(url)) {
          return children
        }

        return '<a href="' + escape(url) + '"' +
          (node.title ? ' title="' + escape(node.title) + '"' : '') +
          (node.target ? ' target="' + escape(node.target) + '"' : '') +
          '>' + children + '</a>'
      }

      case 'text': {
        var text = escape(node.value).replace(/\n/g, '<br>')

        if (node.bold) {
          text = '<strong>' + text + '</strong>'
        }

        if (node.italic) {
          text = '<em>' + text + '</em>'
        }

        return text
      }

      default:
        return children
    }
  }

  /**
   * Convert a rich text JSON string to HTML.
   * @param {*} value - Description as committed.
   * @returns {String|null} HTML, or null when the value is not rich text JSON.
   */
  function toHtml(value) {
    if (typeof value !== 'string' || value.charAt(0) !== '{') {
      return null
    }

    var doc

    try {
      doc = JSON.parse(value)
    } catch (error) {
      return null
    }

    if (!doc || doc.type !== 'root') {
      return null
    }

    return renderNode(doc)
  }

  /**
   * Re-commit JSON descriptions as HTML. The HTML does not start with `{`, so
   * the second commit passes through untouched.
   * @param {Object} store - Vuex store.
   */
  function start(store) {
    store.subscribe(function(mutation) {
      if (mutation.type !== MUTATION) {
        return
      }

      var html = toHtml(mutation.payload)

      if (html !== null) {
        store.commit(MUTATION, html)
      }
    })
  }

  /**
   * Wait for Vue to mount.
   * @param {Number} attempt - Current attempt.
   */
  function boot(attempt) {
    var store = findStore()

    if (store) {
      start(store)
      return
    }

    if (attempt >= BOOT_ATTEMPTS) {
      return
    }

    window.setTimeout(function() {
      boot(attempt + 1)
    }, BOOT_INTERVAL)
  }

  boot(0)
})()
